import React, { useCallback, useContext, useMemo } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import { getTimeRangeWithDates } from '@ubeya/shared/utils/time';
import { Tooltip } from '@ubeya/shared-web/components';
import { API_DATE_TIME_FORMAT, MAX_HOURS_SHOW_PROMPT } from '@ubeya/shared/constants';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { isCellClearable, isCellEditable, isReadOnly } from 'shared/utils/timesheet';
import BaseTimeRenderer from './BaseTimeRenderer';

const OriginalTimesheetHours = styled.div`
  color: ${({ theme }) => theme.colors.redNegative};
`;
const ScheduledTimesheetHours = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey3};
`;

const InTimeRenderer = ({
  rowData,
  columnData: {
    timeFormat,
    updateStartTime,
    updateOSStartTime,
    updateEndTime,
    updateOSEndTime,
    setIsPromptOpen,
    readOnly = false,
    allowUpdateExternal = false,
    showTimesheetOriginalHours = false,
    showTimesheetScheduledHours = false,
    showClientTimesheetOriginalHours = false,
    onOutsideClickSave = false,
    disableWarning = false
  } = {}
}) => {
  const formattingMap = useFormattingMap();

  const { t } = useContext(ConfigContext);

  const myPrivileges = useContext(PrivilegesContext);

  const onChangeEndTime = useCallback(
    ({ endTime }) => {
      const updateFunc = !rowData.isExternal ? updateEndTime : updateOSEndTime;
      if (!updateFunc) return;
      updateFunc({ newTime: endTime, ...rowData });
    },
    [rowData, updateEndTime, updateOSEndTime]
  );

  const onChangeStartTime = useCallback(
    ({ startTime, endTime }) => {
      if (endTime && !moment(endTime, API_DATE_TIME_FORMAT).isSame(moment(rowData.actualOut, API_DATE_TIME_FORMAT))) {
        onChangeEndTime({ endTime });
      }

      return !rowData.isExternal
        ? updateStartTime({ newTime: startTime, ...rowData })
        : updateOSStartTime({ newTime: startTime, ...rowData });
    },
    [rowData, updateStartTime, updateOSStartTime, onChangeEndTime]
  );

  const isBreaktimeIncludedInHoursRange = useMemo(
    () =>
      (rowData?.breaktimes || []).some(({ startTime: breakStartTime }) =>
        moment(breakStartTime, API_DATE_TIME_FORMAT).isBefore(moment(rowData?.actualIn, API_DATE_TIME_FORMAT))
      )
        ? t('breakStartBeforeThisHour')
        : null,
    [rowData.actualIn, rowData?.breaktimes, t]
  );

  const handleUpdateTimeSheet = useCallback(
    (newTime, originalNewTime) => {
      const { startTime } = getTimeRangeWithDates({ date: rowData.date, startTime: newTime, checkBaseStartDate: true });

      let endTime = rowData.actualOut;
      if (rowData.actualOut) {
        const timeValues = getTimeRangeWithDates({
          date: rowData.date,
          startTime,
          endTime: rowData.actualOut,
          checkBaseStartDate: true,
          includeEqualStartEndTIme: false
        });

        endTime = timeValues.endTime;
      }

      if (newTime) {
        const duration = endTime && moment.duration(moment(endTime).diff(startTime, 'milliseconds')).asHours();
        if (duration && duration > MAX_HOURS_SHOW_PROMPT && !!setIsPromptOpen) {
          return setIsPromptOpen({
            onConfirm: () => onChangeStartTime({ startTime, endTime }),
            duration
          });
        }

        if (!moment(startTime, API_DATE_TIME_FORMAT).isSame(moment(rowData.actualIn, API_DATE_TIME_FORMAT))) {
          onChangeStartTime({ startTime, endTime });
        }
      } else if (originalNewTime === null) {
        onChangeStartTime({ startTime, endTime });
      }
    },
    [rowData.date, rowData.actualOut, rowData.actualIn, setIsPromptOpen, onChangeStartTime]
  );

  const isEditable = isCellEditable({
    readOnly: isReadOnly(rowData, readOnly),
    updateFunc: rowData?.isExternal ? updateOSStartTime : updateStartTime,
    isExternal: rowData?.isExternal,
    allowUpdateExternal
  });

  const isClearable = isCellClearable({
    isClientApproved: rowData?.clientApproved && rowData?.isExternal,
    isApproved: rowData?.approved,
    isUnlockPayrollPrivilege: myPrivileges[privileges.UNLOCK_PAYROLL],
    // this cell is clearable if the clock out time does not have value
    allowClear: !rowData.actualOut
  });

  const showTimesheetHoursUnderTime = useMemo(() => {
    if (!showTimesheetOriginalHours || !rowData.actualIn || !rowData.timesheetStartTime) {
      return false;
    }

    const actualInMoment = moment(rowData.actualIn, 'YYYY-MM-DD HH:mm:ss');
    const timesheetStartTimeMoment = moment(rowData.timesheetStartTime, 'YYYY-MM-DD HH:mm:ss');

    actualInMoment.startOf('minute');
    timesheetStartTimeMoment.startOf('minute');

    return !actualInMoment.isSame(timesheetStartTimeMoment);
  }, [rowData.actualIn, rowData.timesheetStartTime, showTimesheetOriginalHours]);

  const showClientTimesheetHoursUnderTime = useMemo(() => {
    if (!showClientTimesheetOriginalHours || !rowData.actualIn || !rowData.clientActualIn) {
      return false;
    }

    const actualInMoment = moment(rowData.actualIn, 'YYYY-MM-DD HH:mm:ss');
    const clientActualInMoment = moment(rowData.clientActualIn, 'YYYY-MM-DD HH:mm:ss');

    actualInMoment.startOf('minute');
    clientActualInMoment.startOf('minute');

    return !actualInMoment.isSame(clientActualInMoment);
  }, [rowData.actualIn, rowData.clientActualIn, showClientTimesheetOriginalHours]);

  const showTimesheetScheduledHoursUnderTime = showTimesheetScheduledHours && rowData.scheduleIn;

  return (
    <>
      <BaseTimeRenderer
        isIn
        showWarning={rowData.actualIn && isBreaktimeIncludedInHoursRange}
        disableWarning={disableWarning}
        tooltipBody={rowData.actualIn && formattingMap.dateAndTime(rowData.actualIn)}
        rowData={rowData}
        actualValue={rowData.actualIn}
        actualUserValue={rowData.userActualIn}
        actualReporter={rowData.actualInReporter}
        reportMethod={rowData.reportMethods?.in}
        reportedValueMethod={rowData.reportMethods?.reportedIn}
        scheduleValue={rowData.scheduleIn}
        onChangeActualValue={handleUpdateTimeSheet}
        isEditable={isEditable}
        onOutsideClickSave={onOutsideClickSave}
        clearable={isClearable}
        visible={!rowData.actualOut}
      />

      {showTimesheetHoursUnderTime && (
        <Tooltip body={t('originalTimesheetStartTime')}>
          <OriginalTimesheetHours>
            {moment(rowData.timesheetStartTime).format(timeFormat || 'hh:mm')}
          </OriginalTimesheetHours>
        </Tooltip>
      )}

      {showTimesheetScheduledHoursUnderTime && (
        <Tooltip body={t('scheduledTimesheetStartTime')}>
          <ScheduledTimesheetHours>{moment(rowData.scheduleIn).format(timeFormat || 'hh:mm')}</ScheduledTimesheetHours>
        </Tooltip>
      )}

      {showClientTimesheetHoursUnderTime && (
        <Tooltip body={t('originalClientTimesheetStartTime')}>
          <OriginalTimesheetHours>
            {moment(rowData.clientActualIn).format(timeFormat || 'hh:mm')}
          </OriginalTimesheetHours>
        </Tooltip>
      )}
    </>
  );
};

export default InTimeRenderer;
